import React, { useState } from 'react';
import { Smartphone, Watch, Heart, Activity, Thermometer, Droplets, Zap, Bluetooth, Wifi, RefreshCw } from 'lucide-react';
import ComingSoonBanner from '../common/ComingSoonBanner';
import { MockBadge } from '../common/MockBadge';
import { DataDisclaimer } from '../common/DataDisclaimer';
import { useExampleData } from '../../data/example/ExampleDataProvider';
import { exampleConnectedDevices, exampleTelemetryData, ConnectedDevice, TelemetryData } from '../../data/example/deviceData';

export const DeviceIntegration: React.FC = () => {
  const { getConnectedDevices, getTelemetryData, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('device-integration');
  const [connectedDevices, setConnectedDevices] = useState<ConnectedDevice[]>(() => isExampleData ? getConnectedDevices() : []);
  const [recentData] = useState<TelemetryData[]>(() => isExampleData ? getTelemetryData() : []);

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'glucose_monitor': return Droplets;
      case 'heart_monitor': return Heart;
      case 'fitness_tracker': return Watch;
      case 'smart_scale': return Activity;
      case 'blood_pressure': return Heart;
      case 'pulse_oximeter': return Thermometer;
      default: return Smartphone;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'text-green-600 bg-green-50';
      case 'disconnected': return 'text-red-600 bg-red-50';
      case 'syncing': return 'text-blue-600 bg-blue-50';
      case 'error': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <Wifi className="h-4 w-4 text-green-500" />;
      case 'disconnected': return <Wifi className="h-4 w-4 text-red-500" />;
      case 'syncing': return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error': return <Bluetooth className="h-4 w-4 text-orange-500" />;
      default: return <Smartphone className="h-4 w-4 text-gray-500" />;
    }
  };

  const getBatteryColor = (level: number) => {
    if (level > 60) return 'text-green-600';
    if (level > 30) return 'text-yellow-600';
    return 'text-red-600';
  };

  const deviceStats = {
    connected: connectedDevices.filter(d => d.status === 'connected').length,
    totalDataPoints: connectedDevices.reduce((sum, d) => sum + d.dataPoints, 0),
    recentSyncs: connectedDevices.filter(d => 
      new Date(d.lastSync) > new Date(Date.now() - 24 * 60 * 60 * 1000)
    ).length,
  };

  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <ComingSoonBanner 
        featureName="Device Integration Hub"
        description="Your device hub will support 100+ health devices with real-time syncing, automatic data validation, cloud backup, and seamless integration with your healthcare providers and emergency services."
        currentPage="/devices"
        variant="compact"
        expectedRelease="very soon"
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <h2 className="text-3xl font-bold text-slate-800">Device Integration</h2>
          {isExampleData && <MockBadge variant="small" className="ml-2" />}
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <Bluetooth className="h-4 w-4" />
            <span>Add Device</span>
          </button>
          <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <RefreshCw className="h-4 w-4" />
            <span>Sync All</span>
          </button>
        </div>
      </div>
      {isExampleData && (
        <DataDisclaimer 
          type="info"
          title="Example Devices"
          message="These are example devices and data for demonstration. Real device integration will be available when you connect your health devices."
          className="mb-4"
        />
      )}
      {/* Device Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Connected Devices</p>
              <p className="text-2xl font-bold text-slate-800">{deviceStats.connected}</p>
            </div>
            <Bluetooth className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Data Points</p>
              <p className="text-2xl font-bold text-slate-800">{deviceStats.totalDataPoints.toLocaleString()}</p>
            </div>
            <Activity className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Recent Syncs (24h)</p>
              <p className="text-2xl font-bold text-slate-800">{deviceStats.recentSyncs}</p>
            </div>
            <RefreshCw className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Demo Mode</p>
              <p className="text-2xl font-bold text-slate-800">{isExampleData ? 'On' : 'Off'}</p>
            </div>
            <Zap className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
      </div>
      {/* Devices List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {connectedDevices.map(device => {
          const Icon = getDeviceIcon(device.type);
          return (
            <div key={device.id} className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col space-y-2 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Icon className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">{device.name}</h3>
                  <p className="text-xs text-slate-500">{device.brand}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(device.status)}`}>{device.status}</span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-slate-500">
                <span className="flex items-center space-x-1">
                  {getStatusIcon(device.status)}
                  <span>{device.status}</span>
                </span>
                {device.batteryLevel !== undefined && (
                  <span className={`flex items-center space-x-1 ${getBatteryColor(device.batteryLevel)}`}>
                    <Zap className="h-4 w-4" />
                    <span>{device.batteryLevel}%</span>
                  </span>
                )}
                <span className="flex items-center space-x-1">
                  <Activity className="h-4 w-4" />
                  <span>{device.dataPoints} pts</span>
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {device.metrics.map(metric => (
                  <span key={metric} className="px-2 py-1 bg-slate-100 text-xs text-slate-600 rounded-full">
                    {metric}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {/* Recent Data Table */}
      <div className="bg-white rounded-lg p-4 border border-slate-200 mt-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Recent Device Data</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-4 py-2 text-left font-medium text-slate-600">Time</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Device</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Metric</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Value</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Source</th>
              </tr>
            </thead>
            <tbody>
              {recentData.map((data, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-4 py-2">{new Date(data.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-2">{data.deviceId}</td>
                  <td className="px-4 py-2">{data.metric}</td>
                  <td className="px-4 py-2">{data.value} {data.unit}</td>
                  <td className="px-4 py-2">{data.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
